import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Alert from "../../../components/common/Alert";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import Input from "../../../components/common/Input";
import MultiSelect from "../../../components/common/MultiSelect";
import PageHeader from "../../../components/common/PageHeader";
import Select from "../../../components/common/Select";
import StatCard from "../../../components/common/StatCard";
import Table from "../../../components/common/Table";
import Section from "../../../components/common/Section";
import { useGetBusinessStoresQuery } from "../../../store/api";
import { getStoreOptionsFromBusinesses } from "../../../utils/reporting";
import {
  CASHING_UP_STATUS_OPTIONS,
  getCashingUpExportParams,
  getDefaultCashingUpFilters,
  normalizeCashingUpCollection,
} from "../../../utils/cashingUpReporting";
import { downloadFile } from "../../../utils/downloadFile";

const formatDateValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseCsvParam = (searchParams, key) =>
  searchParams
    .getAll(key)
    .flatMap((value) => String(value).split(","))
    .map((value) => value.trim())
    .filter(Boolean);

const buildCashingUpSearchParams = (
  filters,
  { showBusinessDate, showDateRange, showTillId, showStatuses, showLimit } = {},
) => {
  const params = new URLSearchParams();

  if (filters.storeId) {
    params.set("storeId", filters.storeId);
  }

  if (showBusinessDate && filters.businessDate) {
    params.set("businessDate", filters.businessDate);
  }

  if (showDateRange && filters.from) {
    params.set("from", filters.from);
  }

  if (showDateRange && filters.to) {
    params.set("to", filters.to);
  }

  if (showTillId && filters.tillId) {
    params.set("tillId", filters.tillId);
  }

  if (showStatuses && Array.isArray(filters.statuses)) {
    filters.statuses.forEach((status) => {
      if (status) {
        params.append("status", status);
      }
    });
  }

  if (showLimit && filters.limit) {
    params.set("limit", String(filters.limit));
  }

  return params;
};

const getCashingUpFiltersFromSearchParams = (searchParams) => {
  const defaults = getDefaultCashingUpFilters();
  const limit = Number(searchParams.get("limit"));

  return {
    ...defaults,
    storeId: searchParams.get("storeId") || "",
    businessDate: searchParams.get("businessDate") || defaults.businessDate,
    from: searchParams.get("from") || defaults.from,
    to: searchParams.get("to") || defaults.to,
    tillId: searchParams.get("tillId") || "",
    statuses: parseCsvParam(searchParams, "status"),
    limit: Number.isFinite(limit) && limit > 0 ? limit : defaults.limit,
  };
};

const CashingUpReportPage = ({
  title,
  subtitle,
  useQueryHook,
  collectionKeys = [],
  columns = [],
  buildRows,
  buildStats,
  emptyText = "No records found for the selected filters.",
  showBusinessDate = true,
  showDateRange = false,
  showTillId = false,
  showStatuses = false,
  showLimit = true,
  showExport = false,
  exportFileName = "cashing-up-report.csv",
  exportEndpoint = "/api/reports/cashing-up/export",
  buildExportParams,
  statusOptions = CASHING_UP_STATUS_OPTIONS,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = useSelector((state) => state.auth.token);
  const currentBusinessId = useSelector(
    (state) => state.auth.user?.businessId || state.license?.businessId || null,
  );
  const { data: businessStores, isLoading: isLoadingStores } =
    useGetBusinessStoresQuery(currentBusinessId, {
      skip: !currentBusinessId,
    });
  const storeOptions = useMemo(
    () => getStoreOptionsFromBusinesses([{ stores: businessStores || [] }]),
    [businessStores],
  );

  const [draftFilters, setDraftFilters] = useState(() =>
    getCashingUpFiltersFromSearchParams(searchParams),
  );
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getCashingUpFiltersFromSearchParams(searchParams),
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [filterError, setFilterError] = useState("");
  const todayDateValue = useMemo(() => getTodayDateValue(), []);
  const compactFilterRow = showStatuses || showDateRange;
  const filterFieldClassName = compactFilterRow
    ? "lg:col-span-1 xl:col-span-2"
    : "lg:col-span-1 xl:col-span-4";
  const filterGridClassName = compactFilterRow
    ? "grid gap-4 lg:grid-cols-2 xl:grid-cols-10 xl:items-end"
    : "grid gap-4 lg:grid-cols-2 xl:grid-cols-12 xl:items-end";
  const resetButtonClassName = compactFilterRow
    ? "flex lg:col-span-1 xl:col-span-2 xl:justify-end"
    : "flex lg:col-span-1 xl:col-span-4";

  useEffect(() => {
    if (!storeOptions.length) return;
    if (draftFilters.storeId) return;
    const defaultStoreId = storeOptions[0]?.id || "";
    if (defaultStoreId) {
      const next = {
        ...draftFilters,
        storeId: defaultStoreId,
      };
      setDraftFilters(next);
      setAppliedFilters(next);
    }
  }, [draftFilters, storeOptions]);

  useEffect(() => {
    setSearchParams(
      buildCashingUpSearchParams(appliedFilters, {
        showBusinessDate,
        showDateRange,
        showLimit,
        showStatuses,
        showTillId,
      }),
      { replace: true },
    );
  }, [
    appliedFilters,
    setSearchParams,
    showBusinessDate,
    showDateRange,
    showLimit,
    showStatuses,
    showTillId,
  ]);

  const queryArgs = useMemo(() => {
    if (!appliedFilters.storeId) {
      return null;
    }
    return {
      storeId: appliedFilters.storeId,
      businessDate: showBusinessDate ? appliedFilters.businessDate : undefined,
      from: showDateRange ? appliedFilters.from : undefined,
      to: showDateRange ? appliedFilters.to : undefined,
      tillId: showTillId ? appliedFilters.tillId : undefined,
      statuses: showStatuses ? appliedFilters.statuses : [],
      limit: showLimit ? appliedFilters.limit : undefined,
    };
  }, [
    appliedFilters,
    showBusinessDate,
    showDateRange,
    showLimit,
    showStatuses,
    showTillId,
  ]);

  const queryResult = useQueryHook(queryArgs, {
    skip: !queryArgs,
  });
  const { data, isLoading, isFetching, isError, error } = queryResult;

  const normalizedRows = useMemo(() => {
    const collection = normalizeCashingUpCollection(data, collectionKeys);
    return typeof buildRows === "function" ? buildRows(collection, data) : [];
  }, [buildRows, collectionKeys, data]);

  const currencyCode = data?.currencyCode || "USD";
  const summaryCards = useMemo(() => {
    if (typeof buildStats !== "function") {
      return [];
    }
    return buildStats(data, normalizedRows, currencyCode) || [];
  }, [buildStats, currencyCode, data, normalizedRows]);

  const handleReset = () => {
    const defaultStoreId = storeOptions[0]?.id || "";
    const next = {
      ...getDefaultCashingUpFilters(),
      storeId: defaultStoreId,
    };
    setDraftFilters(next);
    setAppliedFilters(next);
    setDownloadError("");
    setFilterError("");
  };

  const handleFilterChange = (field, value) => {
    const next = {
      ...draftFilters,
      [field]: value,
    };

    const isDateField = ["businessDate", "from", "to"].includes(field);
    if (isDateField && value && value > todayDateValue) {
      setFilterError("Future dates are not allowed.");
      return;
    }

    if (showDateRange && next.from && next.to && next.from > next.to) {
      setFilterError("From date cannot be after To date.");
      setDraftFilters(next);
      return;
    }

    setFilterError("");
    setDraftFilters(next);
    setAppliedFilters(next);
  };

  const handleDownload = async () => {
    if (!showExport) return;
    try {
      setIsDownloading(true);
      setDownloadError("");
      const params =
        typeof buildExportParams === "function"
          ? buildExportParams(appliedFilters)
          : getCashingUpExportParams(appliedFilters);
      await downloadFile({
        endpoint: exportEndpoint,
        queryParams: params,
        token,
        fallbackFileName: exportFileName,
      });
    } catch (err) {
      if (err?.status === 401) {
        setDownloadError("Session expired. Please log in again.");
        navigate("/login");
      } else {
        setDownloadError("Failed to download CSV. Please try again.");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          showExport ? (
            <Button
              onClick={handleDownload}
              disabled={isDownloading || !appliedFilters.storeId}
              className="h-9 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-[0_8px_16px_rgba(79,70,229,0.28)] hover:bg-indigo-700"
            >
              {isDownloading ? "Downloading..." : "Export CSV"}
            </Button>
          ) : null
        }
      />

      <Card className="p-6">
        <Section title="Filters">
          <div className={filterGridClassName}>
            <Select
              label="Store"
              value={draftFilters.storeId}
              onChange={(event) =>
                handleFilterChange("storeId", event.target.value)
              }
              containerClassName={filterFieldClassName}
            >
              <option value="">
                {isLoadingStores ? "Loading stores..." : "Select a store"}
              </option>
              {storeOptions.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                  {store.businessName ? ` (${store.businessName})` : ""}
                </option>
              ))}
            </Select>

            {showBusinessDate && (
              <Input
                label="Business Date"
                type="date"
                max={todayDateValue}
                value={formatDateValue(draftFilters.businessDate)}
                onChange={(event) =>
                  handleFilterChange("businessDate", event.target.value)
                }
                containerClassName={filterFieldClassName}
              />
            )}

            {showDateRange && (
              <>
                <Input
                  label="From"
                  type="date"
                  max={todayDateValue}
                  value={formatDateValue(draftFilters.from)}
                  onChange={(event) =>
                    handleFilterChange("from", event.target.value)
                  }
                  containerClassName={filterFieldClassName}
                />
                <Input
                  label="To"
                  type="date"
                  max={todayDateValue}
                  value={formatDateValue(draftFilters.to)}
                  onChange={(event) =>
                    handleFilterChange("to", event.target.value)
                  }
                  containerClassName={filterFieldClassName}
                />
              </>
            )}

            {showTillId && (
              <Input
                label="Till ID"
                type="text"
                value={draftFilters.tillId}
                onChange={(event) =>
                  handleFilterChange("tillId", event.target.value)
                }
                placeholder="Optional till identifier"
                containerClassName={filterFieldClassName}
              />
            )}

            {showLimit && (
              <Input
                label="Limit"
                type="number"
                value={draftFilters.limit}
                onChange={(event) =>
                  handleFilterChange("limit", Number(event.target.value) || 20)
                }
                helper="Maximum number of rows returned by the API."
                containerClassName={filterFieldClassName}
              />
            )}
            {showStatuses && (
              <MultiSelect
                label="Status"
                options={statusOptions.map((status) => ({
                  id: status.value,
                  name: status.label,
                }))}
                selected={draftFilters.statuses}
                onChange={(selected) => handleFilterChange("statuses", selected)}
                containerClassName={filterFieldClassName}
              />
            )}
            <div className={resetButtonClassName}>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReset}
                className="h-11 w-full px-5"
              >
                Reset
              </Button>
            </div>
          </div>

          {filterError ? (
            <div className="mt-4">
              <Alert tone="error">{filterError}</Alert>
            </div>
          ) : null}

          {downloadError ? (
            <div className="mt-4">
              <Alert tone="error">{downloadError}</Alert>
            </div>
          ) : null}
        </Section>

        {isError && (
          <div className="mt-6">
            <Alert tone="error">
              {error?.data?.detail || "Failed to load cashing-up report."}
            </Alert>
          </div>
        )}

        {summaryCards.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-3">
            {summaryCards.map((card) => (
              <div key={card.label} className="w-full sm:w-[180px] xl:w-[200px]">
                <StatCard
                  label={card.label}
                  value={card.value}
                  accent={card.accent || "indigo"}
                  compact
                />
              </div>
            ))}
          </div>
        )}

        <div className="mt-5">
          {isLoading || isFetching ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((value) => (
                <div
                  key={value}
                  className="h-12 animate-pulse rounded-lg bg-slate-100"
                />
              ))}
            </div>
          ) : normalizedRows.length === 0 ? (
            <Alert tone="info">{emptyText}</Alert>
          ) : (
            <Table
              columns={columns}
              rows={normalizedRows.map((row, index) => ({
                id:
                  row.id ||
                  row.cashSessionId ||
                  row.cashMovementId ||
                  row.dayCloseId ||
                  index,
                ...row,
              }))}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default CashingUpReportPage;
